import { useState, useEffect, useCallback } from "react";
import WidgetCard from "../Components/WidgetCard";
import GuestListTable from "../Components/Tables/GuestListTable";
import axios from "axios";
import {
  GUEST_SUMMARY_API,
  GUEST_LIST_API,
  ALL_TABLE_OPTIONS_API,
  TAG_OPTIONS_API,
} from "../Constant/api";
import { useToast } from "../Components/Toast";
import LoadingInComponent from "../Components/LoadingInComponent";
import Can from "../Components/Can";
import { statusOptions } from "../Constant/options";

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [guestList, setGuestList] = useState([]);
  const [guestListMeta, setGuestListMeta] = useState({});
  const [guestSummary, setGuestSummary] = useState();
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchCleared, setIsSearchCleared] = useState(false);
  const [perPage, setPerPage] = useState(10);
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortBy, setSortBy] = useState("id");
  const [tagOptions, setTagOptions] = useState([]);
  const [tableOptions, setTableOptions] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]);

  const { toast } = useToast();

  const handleSortOrderChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const handlePageClick = (e) => {
    fetchGuestList(e.selected + 1);
  };

  const handlePerPageChange = (e) => {
    setPerPage(e.target.value);
    fetchGuestList(1, e.target.value);
  };

  const clearFilter = () => {
    setSearchTerm("");
    setSelectedTables([]);
    setSelectedTags([]);
    setSelectedStatus([]);
    setIsSearchCleared(true);
  };

  const formatDaysRemaining = (daysRemaining) => {
    if (daysRemaining === null) {
      return "N/A";
    } else {
      if (daysRemaining === 0) {
        return "Today";
      } else if (daysRemaining < 0) {
        return "Ended";
      } else {
        return daysRemaining;
      }
    }
  };

  const fetchGuestList = useCallback(
    async (page = 1, perPageValue = perPage) => {
      setLoading(true);
      try {
        axios
          .get(GUEST_LIST_API, {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
            },
            params: {
              eventId: sessionStorage.getItem("event_id"),
              page: page,
              perPage: perPageValue,
              search: searchTerm,
              sortBy,
              sortOrder: sortOrder,
              tags: selectedTags,
              tables: selectedTables,
              statuses: selectedStatus,
            },
          })
          .then((res) => {
            setLoading(false);
            setGuestList(res.data.data);
            setGuestListMeta(res.data.meta.pagination);
          })
          .catch((err) => {
            setLoading(false);
            toast("Failed to fetch guest list.", "error");
            console.log(err);
          });
      } catch (error) {
        setLoading(false);
        toast("Failed to fetch guest list.", "error");
        console.log(error);
      }
    },
    [
      perPage,
      searchTerm,
      sortBy,
      sortOrder,
      selectedTags,
      selectedTables,
      selectedStatus,
      toast,
    ]
  );

  const fetchGuestSummary = useCallback(async () => {
    setLoading(true);
    try {
      axios
        .get(GUEST_SUMMARY_API, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
          params: { eventId: sessionStorage.getItem("event_id") },
        })
        .then((res) => {
          setLoading(false);
          setGuestSummary(res.data.data);
        })
        .catch((err) => {
          setLoading(false);
          toast("Failed to fetch guest summary.", "error");
          console.log(err);
        });
    } catch (error) {
      setLoading(false);
      toast("Failed to fetch guest summary.", "error");
      console.log(error);
    }
  }, [toast]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      fetchGuestList();
    }
  };

  const fetchTagList = useCallback(async () => {
    setLoading(true);
    axios
      .get(TAG_OPTIONS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: { eventId: sessionStorage.getItem("event_id") },
      })
      .then((res) => {
        setLoading(false);
        setTagOptions(res.data.data);
      })
      .catch((err) => {
        setLoading(false);
        toast("Failed to fetch tag list.", "error");
        console.log(err);
      });
  }, [toast]);

  const fetchTableList = useCallback(async () => {
    setLoading(true);
    axios
      .get(ALL_TABLE_OPTIONS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: { eventId: sessionStorage.getItem("event_id") },
      })
      .then((res) => {
        setLoading(false);
        setTableOptions(res.data.data);
      })
      .catch((err) => {
        setLoading(false);
        toast("Failed to fetch table list.", "error");
        console.log(err);
      });
  }, [toast]);

  const handleDropdownChange = (selectedOptions, type) => {
    if (type === "tags") {
      setSelectedTags(selectedOptions);
    } else if (type === "tables") {
      setSelectedTables(selectedOptions);
    } else if (type === "status") {
      setSelectedStatus(selectedOptions);
    }
  };

  useEffect(() => {
    fetchGuestList();
    fetchGuestSummary();
    fetchTagList();
    fetchTableList();
  }, [
    fetchGuestList,
    fetchGuestSummary,
    fetchTagList,
    fetchTableList,
    sortOrder,
    selectedTags,
    selectedTables,
    selectedStatus,
  ]);

  useEffect(() => {
    if (isSearchCleared && searchTerm === "") {
      fetchGuestList();
      setIsSearchCleared(false);
    }
  }, [isSearchCleared, searchTerm, fetchGuestList]);

  return (
    <div className="flex flex-col w-full h-full overflow-x-hidden">
      <LoadingInComponent isLoading={loading}>
        <div className="flex flex-col w-full h-full p-[16px] md:p-[32px] overflow-y-auto gap-[12px] md:gap-[24px]">
          {/* Title */}
          <div className="flex flex-col md:flex-row w-full justify-between items-center gap-4">
            <h1 className="text-[29px]">Dashboard</h1>
          </div>

          {/* Widget */}
          {guestSummary && (
            <div className="grid grid-cols-1 md:grid-cols-3 w-full gap-[12px] md:gap-[24px]">
              <WidgetCard
                name="Total Guests"
                data={guestSummary.total_guests}
              />
              <WidgetCard
                name="Total Checked in Guests"
                data={guestSummary.total_checked_in}
              />
              <WidgetCard
                name="Coming Days"
                data={formatDaysRemaining(guestSummary.days_remaining)}
              />
            </div>
          )}
          {/* Data */}
          {(!loading || guestList.length > 0) && (
            <div>
              <Can permission="view_guest_list">
                <GuestListTable
                  withHeaderAction
                  searchable
                  data={guestList}
                  meta={guestListMeta}
                  handlePageClick={handlePageClick}
                  handlePerPageChange={handlePerPageChange}
                  searchTerm={searchTerm}
                  handleSearch={handleSearch}
                  handleKeyDown={handleKeyDown}
                  perPage={perPage}
                  clearFilter={clearFilter}
              sortBy={sortBy}
              sortOrder={sortOrder}
              handleSortOrderChange={handleSortOrderChange}
                  tagOptions={tagOptions}
                  tableOptions={tableOptions}
                  statusOptions={statusOptions}
                  selectedTags={selectedTags}
                  selectedTables={selectedTables}
                  selectedStatus={selectedStatus}
                  handleDropdownChange={handleDropdownChange}
                />
              </Can>
            </div>
          )}
        </div>
      </LoadingInComponent>
    </div>
  );
};

export default Dashboard;
